import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, Upload, Plus, ExternalLink, X, ArrowUpRight, FolderPlus, GripVertical, Pencil, Check } from "lucide-react";
import { api, openFile } from "@/lib/api";
import { OVERVIEW, SECTION_META } from "@/lib/nvsnContent";
import TeamSection from "@/components/dataroom/TeamSection";
import CapTable from "@/components/dataroom/CapTable";
import ObjectionMemo from "@/components/dataroom/ObjectionMemo";
import RunClubMap from "@/components/dataroom/RunClubMap";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "");
const fmtSize = (b) => (!b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const iconClass = (ext) => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return "bg-[rgba(220,60,60,0.12)] text-[#dc3c3c] border-[rgba(220,60,60,0.2)]";
  if (["ppt", "pptx", "key"].includes(e)) return "bg-[rgba(220,100,40,0.12)] text-[#dc6428] border-[rgba(220,100,40,0.2)]";
  if (["xls", "xlsx", "csv"].includes(e)) return "bg-[rgba(40,160,80,0.12)] text-[#28a050] border-[rgba(40,160,80,0.2)]";
  if (["doc", "docx", "txt"].includes(e)) return "bg-[rgba(40,100,220,0.12)] text-[#2864dc] border-[rgba(40,100,220,0.2)]";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(e)) return "bg-[rgba(140,60,200,0.12)] text-[#8c3cc8] border-[rgba(140,60,200,0.2)]";
  return "bg-[#1a1a1a] text-[#8f8f8f] border-[#222]";
};
const iconLabel = (ext) => (ext || "FILE").toUpperCase().slice(0, 4);

function ExecSummary({ onNavigate }) {
  return (
    <>
      <div className="flex flex-wrap gap-x-10 gap-y-4 justify-center py-6 mb-11">
        {OVERVIEW.stats.map((s) => (
          <div key={s.label} className="flex flex-col gap-0.5 items-center">
            <div className="font-mono-dm text-[16px] text-[#f0f0f0]">{s.value}</div>
            <div className="mono-eyebrow text-[#bdbdbd]">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="mb-12">
        {OVERVIEW.summary.map((b, i) => (
          <div key={b.label} className={`grid md:grid-cols-[160px_1fr] gap-4 md:gap-8 py-7 ${i === 0 ? "pt-0" : ""}`}>
            <div className="font-mono-dm uppercase tracking-[0.1em] text-[10px] text-[#4deb7a] pt-1">{b.label}</div>
            <div className="text-[13px] text-[#d4d4d4] leading-[1.8] whitespace-pre-line">{b.body}</div>
          </div>
        ))}
      </div>
      <div className="mono-eyebrow text-[#bdbdbd] mb-4">Six Pillars · One System</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {OVERVIEW.pillars.map((p) => (
          <button key={p.num} type="button" data-testid={`pillar-link-${p.name.toLowerCase()}`} onClick={() => onNavigate?.(p.name.toLowerCase())}
            className="group text-left bg-[#0e0e0e] border border-[#222] rounded p-5 hover:border-[#4deb7a] hover:bg-[rgba(77,235,122,0.08)] transition-colors cursor-pointer">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-dm text-[10px] text-[#8f8f8f]">{p.num}</div>
              <ArrowUpRight className="h-3.5 w-3.5 text-[#8f8f8f] opacity-0 group-hover:opacity-100 group-hover:text-[#4deb7a] transition-all" strokeWidth={1.5} />
            </div>
            <div className="text-[13px] text-[#f0f0f0] font-medium mb-1.5 group-hover:text-[#4deb7a] transition-colors">{p.name}</div>
            <div className="text-[11px] text-[#bdbdbd] leading-[1.5] mb-2.5">{p.sub}</div>
            <div className="font-mono-dm text-[10px] text-[#4deb7a] tracking-[0.04em]">{p.launch}</div>
          </button>
        ))}
      </div>
    </>
  );
}

export default function ContentView({ section, isAdmin, onChanged, onNavigateSlug }) {
  const [linkTarget, setLinkTarget] = useState(null); // group id ("root" or sub.id) whose link form is open
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "" });
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSubTitle, setNewSubTitle] = useState("");
  const [editingSub, setEditingSub] = useState(null); // {id, title}
  const [subs, setSubs] = useState(section.subsections || []);
  const [dragId, setDragId] = useState(null);
  const meta = SECTION_META[section.slug] || {};

  useEffect(() => { setSubs(section.subsections || []); }, [section.id, section.subsections]);

  const filesFor = (gid) => (section.files || []).filter((f) => (f.subsection_id || null) === gid);
  const linksFor = (gid) => (section.links || []).filter((l) => (l.subsection_id || null) === gid);

  const doUpload = async (e, gid) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (gid) fd.append("subsection_id", gid);
    try {
      await api.post(`/admin/sections/${section.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/sections/${section.id}/links`, { ...linkForm, subsection_id: linkTarget === "root" ? null : linkTarget });
      toast.success("Link added");
      setLinkForm({ title: "", url: "", description: "" });
      setLinkTarget(null);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add link"); }
  };

  const addSub = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/sections/${section.id}/subsections`, { title: newSubTitle });
      toast.success("Content section added");
      setNewSubTitle(""); setShowAddSub(false);
      onChanged?.();
    } catch (err) { toast.error("Failed to add content section"); }
  };

  const saveSubTitle = async () => {
    try {
      await api.put(`/admin/subsections/${editingSub.id}`, { title: editingSub.title });
      setEditingSub(null); onChanged?.();
    } catch { toast.error("Failed to rename"); }
  };

  const delSub = async (id) => {
    if (!window.confirm("Delete this content section? Its files and links will move back to the top.")) return;
    await api.delete(`/admin/subsections/${id}`);
    toast.success("Content section removed");
    onChanged?.();
  };

  const delFile = async (id) => { await api.delete(`/admin/files/${id}`); toast.success("File removed"); onChanged?.(); };
  const delLink = async (id) => { await api.delete(`/admin/links/${id}`); toast.success("Link removed"); onChanged?.(); };
  const preview = async (f) => { try { await openFile(f); } catch { toast.error("Unable to open file"); } };
  const trackLink = (l) => { if (!isAdmin) api.post(`/links/${l.id}/view`).catch(() => {}); };

  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...subs];
    const from = arr.findIndex((s) => s.id === dragId);
    const to = arr.findIndex((s) => s.id === targetId);
    if (from === -1 || to === -1) { setDragId(null); return; }
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setSubs(arr);
    setDragId(null);
    try {
      await api.put(`/admin/sections/${section.id}/subsections/reorder`, { order: arr.map((s) => s.id) });
      onChanged?.();
    } catch { toast.error("Failed to save order"); }
  };

  const isOverview = section.slug === "overview";
  const empty = (section.files || []).length === 0 && (section.links || []).length === 0 && subs.length === 0;

  const renderFile = (f) => (
    <motion.div key={f.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={() => preview(f)}
      className="group flex items-center gap-3.5 px-4 py-3.5 bg-[#0e0e0e] border border-[#222] rounded hover:bg-[#141414] hover:border-[#2a2a2a] transition-colors cursor-pointer"
      data-testid={`file-row-${f.id}`}>
      <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 font-mono-dm text-[8px] font-medium border ${iconClass(f.ext)}`}>{iconLabel(f.ext)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-[#e8e8e8] truncate">{f.file_name}</div>
        <div className="font-mono-dm text-[10px] text-[#8f8f8f] mt-0.5">{fmtDate(f.uploaded_at)} · {fmtSize(f.size)}</div>
      </div>
      <div className="flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <span className="border border-[#2a2a2a] text-[#8f8f8f] px-2.5 py-1 rounded-sm text-[11px] hover:bg-[#1a1a1a] hover:text-[#e8e8e8] transition-colors">View</span>
        {isAdmin && (
          <button data-testid={`delete-file-${f.id}`} onClick={(e) => { e.stopPropagation(); delFile(f.id); }} className="border border-[#2a2a2a] text-[#8f8f8f] px-2 py-1 rounded-sm hover:border-[rgba(200,50,50,0.4)] hover:text-[#c83232] hover:bg-[rgba(200,50,50,0.08)] transition-colors">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </motion.div>
  );

  const renderLink = (l) => (
    <div key={l.id} className="group flex items-center gap-3.5 px-4 py-3.5 bg-[#0e0e0e] border border-[#222] rounded hover:bg-[#141414] hover:border-[#2a2a2a] transition-colors" data-testid={`link-card-${l.id}`}>
      <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 border border-[rgba(77,235,122,0.2)] bg-[rgba(77,235,122,0.08)] text-[#4deb7a]"><ExternalLink className="h-4 w-4" strokeWidth={1.5} /></div>
      <a href={l.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLink(l)} className="flex-1 min-w-0">
        <div className="text-[13px] text-[#e8e8e8] truncate group-hover:text-[#4deb7a] transition-colors">{l.title}</div>
        {l.description && <div className="text-[11px] text-[#bdbdbd] mt-0.5 leading-snug">{l.description}</div>}
        <div className="font-mono-dm text-[10px] text-[#8f8f8f] mt-1 truncate">{l.url}</div>
      </a>
      <div className="flex items-center gap-2">
        <a href={l.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLink(l)} className="md:opacity-0 md:group-hover:opacity-100 border border-[#2a2a2a] text-[#8f8f8f] px-2.5 py-1 rounded-sm text-[11px] hover:bg-[#1a1a1a] hover:text-[#e8e8e8] transition-all">Open</a>
        {isAdmin && (
          <button data-testid={`delete-link-${l.id}`} onClick={() => delLink(l.id)} className="md:opacity-0 md:group-hover:opacity-100 border border-[#2a2a2a] text-[#8f8f8f] px-2 py-1 rounded-sm hover:border-[rgba(200,50,50,0.4)] hover:text-[#c83232] hover:bg-[rgba(200,50,50,0.08)] transition-all">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );

  const renderControls = (gid) => {
    const key = gid || "root";
    return (
      <div className="mb-3">
        <div className="flex flex-wrap gap-2.5 mb-2">
          <label data-testid={`upload-file-btn-${key}`} className="cursor-pointer inline-flex items-center gap-2 bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] text-[12px] font-medium px-3.5 py-2 rounded transition-colors">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.5} /> Upload file
            <input type="file" className="hidden" onChange={(e) => doUpload(e, gid)} />
          </label>
          <button data-testid={`add-link-btn-${key}`} onClick={() => { setLinkTarget(linkTarget === key ? null : key); setLinkForm({ title: "", url: "", description: "" }); }}
            className="inline-flex items-center gap-2 border border-[rgba(77,235,122,0.2)] bg-[rgba(77,235,122,0.08)] hover:bg-[rgba(77,235,122,0.12)] text-[#4deb7a] text-[12px] font-medium px-3.5 py-2 rounded transition-colors">
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add link
          </button>
        </div>
        {linkTarget === key && (
          <form onSubmit={submitLink} data-testid="link-form" className="border border-[#222] rounded p-4 mb-2 space-y-2.5 bg-[#0e0e0e]">
            <div className="grid sm:grid-cols-2 gap-2.5">
              <input required placeholder="Document name" value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })} className="bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[12px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a]" data-testid="link-title" />
              <input required placeholder="Paste link (Drive, DocSend, Dropbox…)" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} className="bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[12px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a]" data-testid="link-url" />
            </div>
            <input placeholder="Short description (optional)" value={linkForm.description} onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })} className="w-full bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[12px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a]" data-testid="link-desc" />
            <div className="flex gap-2">
              <button type="submit" className="bg-[#4deb7a] text-[#080808] text-[12px] font-medium px-4 py-1.5 rounded" data-testid="save-link">Save link</button>
              <button type="button" onClick={() => setLinkTarget(null)} className="text-[12px] text-[#bdbdbd] px-3 py-1.5 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel</button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderContent = (gid) => {
    const files = filesFor(gid);
    const links = linksFor(gid);
    const groupEmpty = files.length === 0 && links.length === 0;
    return (
      <>
        {isAdmin && renderControls(gid)}
        {groupEmpty && !isAdmin ? null : (
          <div className="flex flex-col gap-0.5">
            {files.map(renderFile)}
            {links.map(renderLink)}
          </div>
        )}
        {isAdmin && groupEmpty && <div className="text-[11px] text-[#8f8f8f] pl-1 pb-1">No content in this group yet.</div>}
      </>
    );
  };

  return (
    <div data-testid="content-view">
      {meta.badge ? (
        <span className="inline-flex items-center gap-1.5 border border-[rgba(77,235,122,0.2)] bg-[rgba(77,235,122,0.08)] text-[#4deb7a] font-mono-dm uppercase tracking-[0.1em] text-[9px] px-2.5 py-1 rounded-sm mb-4">{meta.badge}</span>
      ) : meta.eyebrow ? (
        <div className="font-mono-dm uppercase tracking-[0.14em] text-[10px] text-[#4deb7a] mb-2.5">{meta.eyebrow}</div>
      ) : null}

      <h1 className="font-serif-pf text-[26px] sm:text-[28px] text-[#f0f0f0] leading-tight mb-2.5">{section.title}</h1>
      <p className="text-[13px] text-[#d4d4d4] leading-[1.7] max-w-[580px] mb-6">{section.description}</p>

      {meta.meta && (
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {meta.meta.map((m) => (
            <div key={m.label} className="bg-[#0e0e0e] border border-[#222] rounded p-4">
              <div className="font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] mb-1.5">{m.label}</div>
              <div className="text-[13px] text-[#f0f0f0] leading-[1.5]">{m.value}</div>
            </div>
          ))}
        </div>
      )}

      {isOverview && <ExecSummary onNavigate={onNavigateSlug} />}
      {section.slug === "lifestyle" && <RunClubMap isAdmin={isAdmin} />}

      {section.kind === "team" && <TeamSection section={section} isAdmin={isAdmin} onChanged={onChanged} />}
      {section.kind === "captable" && <CapTable section={section} isAdmin={isAdmin} onChanged={onChanged} />}
      {section.kind === "qa" && <ObjectionMemo section={section} isAdmin={isAdmin} onChanged={onChanged} />}

      {section.kind !== "team" && section.kind !== "captable" && section.kind !== "qa" && (
      <>
      {/* Admin: add content section */}
      {isAdmin && (
        <div className="mb-5">
          {showAddSub ? (
            <form onSubmit={addSub} className="flex gap-2" data-testid="add-subsection-form">
              <input autoFocus required placeholder="Content section title (e.g. Financial Statements)" value={newSubTitle} onChange={(e) => setNewSubTitle(e.target.value)} className="flex-1 bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[12px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a]" data-testid="subsection-title-input" />
              <button type="submit" className="bg-[#4deb7a] text-[#080808] text-[12px] font-medium px-4 py-2 rounded" data-testid="save-subsection">Add</button>
              <button type="button" onClick={() => { setShowAddSub(false); setNewSubTitle(""); }} className="text-[12px] text-[#bdbdbd] px-3">Cancel</button>
            </form>
          ) : (
            <button data-testid="add-subsection-btn" onClick={() => setShowAddSub(true)} className="inline-flex items-center gap-2 border border-dashed border-[#333] hover:border-[#4deb7a] text-[#d4d4d4] hover:text-[#4deb7a] text-[12px] font-medium px-4 py-2.5 rounded transition-colors w-full sm:w-auto justify-center">
              <FolderPlus className="h-4 w-4" strokeWidth={1.5} /> Add content section
            </button>
          )}
        </div>
      )}

      {empty && !isAdmin && (
        <div className="bg-[#0e0e0e] border border-[#222] rounded-md py-12 px-8 text-center" data-testid="empty-state">
          <div className="text-[28px] opacity-30 mb-3">◻</div>
          <div className="text-[13px] text-[#d4d4d4] font-medium mb-1">No documents yet</div>
          <div className="text-[11px] text-[#8f8f8f]">Materials for this section will be published here soon.</div>
        </div>
      )}

      {/* Top-level (ungrouped) content */}
      <div className="mb-6">
        {renderContent(null)}
      </div>

      {/* Sub-sections */}
      {subs.map((sub) => (
        <div key={sub.id}
          draggable={isAdmin && !editingSub}
          onDragStart={() => isAdmin && setDragId(sub.id)}
          onDragOver={(e) => isAdmin && e.preventDefault()}
          onDrop={() => isAdmin && handleDrop(sub.id)}
          className={`mt-8 pt-6 border-t border-[#222] transition-opacity ${dragId === sub.id ? "opacity-40" : ""}`}
          data-testid={`subsection-${sub.id}`}
        >
          <div className="flex items-center gap-2 mb-4">
            {isAdmin && (
              <span data-testid={`drag-handle-${sub.id}`} className="cursor-grab active:cursor-grabbing text-[#666] hover:text-[#bdbdbd]" title="Drag to reorder">
                <GripVertical className="h-4 w-4" strokeWidth={1.5} />
              </span>
            )}
            {isAdmin && editingSub?.id === sub.id ? (
              <div className="flex items-center gap-2 flex-1">
                <input autoFocus value={editingSub.title} onChange={(e) => setEditingSub({ ...editingSub, title: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveSubTitle()} className="flex-1 bg-[#141414] border border-[#2a2a2a] px-2.5 py-1.5 text-[16px] text-[#f0f0f0] rounded focus:outline-none focus:border-[#4deb7a]" data-testid={`edit-subsection-input-${sub.id}`} />
                <button onClick={saveSubTitle} className="text-[#4deb7a]" data-testid={`save-subsection-${sub.id}`}><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditingSub(null)} className="text-[#8f8f8f]"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <>
                <h3 className="font-serif-pf text-[19px] sm:text-[20px] text-[#f0f0f0] flex-1">{sub.title}</h3>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button data-testid={`edit-subsection-${sub.id}`} onClick={() => setEditingSub({ id: sub.id, title: sub.title })} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`delete-subsection-${sub.id}`} onClick={() => delSub(sub.id)} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </>
            )}
          </div>
          {renderContent(sub.id)}
        </div>
      ))}
      </>
      )}
    </div>
  );
}
